import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, AlertTriangle, Hourglass } from "lucide-react";
import { fmtH, mesLabel } from "./portalShared";

export function ConsumoHorasDialog({ open, onClose, saldo, onSaved }) {
  const [form, setForm] = useState({ fecha: "", modo: "dia", horas: 2, motivo: "" });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm({ fecha: "", modo: "dia", horas: 2, motivo: "" }); setPreview(null); setError(null); } }, [open]);
  useEffect(() => {
    if (!open || !form.fecha) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/portal-empleado/solicitudes-horas/preview", { fecha: form.fecha, modo: form.modo, horas: Number(form.horas) || 0 });
        setPreview(data); setError(null);
      } catch (err) { setPreview(null); setError(err?.response?.data?.detail || "No se puede calcular"); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.fecha, form.modo, form.horas]);

  const submit = async () => {
    if (!form.fecha) return toast.error("Indica el día");
    setSaving(true);
    try {
      await api.post("/portal-empleado/solicitudes-horas", { fecha: form.fecha, modo: form.modo, horas: Number(form.horas) || null, motivo: form.motivo });
      toast.success("Solicitud enviada · pendiente de aprobación. No se descuenta nada hasta que se apruebe.");
      onSaved && onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al solicitar"); }
    finally { setSaving(false); }
  };

  const disponible = saldo?.disponible ?? 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-consumo-horas">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Hourglass className="w-4 h-4 text-indigo-300" /> Consumir horas acumuladas</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Disponible: <b className="text-zinc-200" data-testid="consumo-disponible">{fmtH(disponible, false)}</b>
            {saldo?.reservado_en_solicitudes > 0 && <> (−{saldo.reservado_en_solicitudes} h ya en solicitudes pendientes)</>}.
            El saldo no se descuenta hasta que administración apruebe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Día</Label>
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="consumo-fecha" />
            </div>
            <div>
              <Label className="text-xs">Modo</Label>
              <div className="flex rounded-md overflow-hidden border border-zinc-800 h-9">
                {[["dia", "Día completo"], ["horas", "Por horas"]].map(([v, l]) => (
                  <button key={v} onClick={() => setForm({ ...form, modo: v })} data-testid={`consumo-modo-${v}`}
                    className={`flex-1 text-xs ${form.modo === v ? "bg-indigo-500/20 text-indigo-200" : "bg-zinc-900 text-zinc-400"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          {form.modo === "horas" && (
            <div>
              <Label className="text-xs">Horas</Label>
              <Input type="number" min="0.5" step="0.5" value={form.horas} onChange={(e) => setForm({ ...form, horas: e.target.value })} className="bg-zinc-900 border-zinc-800 w-32" data-testid="consumo-horas" />
            </div>
          )}
          <div>
            <Label className="text-xs">Motivo (opcional)</Label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="bg-zinc-900 border-zinc-800 min-h-[50px]" data-testid="consumo-motivo" />
          </div>
          {error && <div className="text-xs text-red-300 flex items-center gap-1" data-testid="consumo-error"><AlertTriangle className="w-3.5 h-3.5" /> {error}</div>}
          {preview && (
            <div className={`text-xs rounded-lg border p-3 space-y-1 ${preview.suficiente ? "border-indigo-500/30 bg-indigo-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="consumo-preview">
              <div>Impacto previsto: <b className="text-zinc-100">−{preview.horas} h</b> del banco · ese día tendrás <b>{form.modo === "dia" ? "0 h" : `${(preview.teorica.minutos / 60 - preview.horas).toFixed(1)} h`}</b> esperadas (jornada teórica {(preview.teorica.minutos / 60).toFixed(1)} h).</div>
              {preview.suficiente ? (
                <div className="text-zinc-400">Se aplicaría a: {preview.aplicacion.map((a) => `${mesLabel(a.periodo)} (${a.horas} h)`).join(", ")}. Quedarían {fmtH(disponible - preview.horas, false)} disponibles.</div>
              ) : (
                <div className="text-red-300 flex items-center gap-1" data-testid="consumo-insuficiente"><AlertTriangle className="w-3.5 h-3.5" /> Saldo insuficiente: necesitas {preview.horas} h y tienes {fmtH(disponible, false)}. No se permite saldo negativo.</div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} disabled={saving || !preview || !preview.suficiente} className="bg-indigo-500 text-zinc-950 hover:bg-indigo-400" data-testid="consumo-submit">
            <Check className="w-4 h-4 mr-2" /> Solicitar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
